/* =========================================================================
   LAYALY LDN — theme.js
   Lightweight, dependency-free interactions. Mobile-first.
   ========================================================================= */
(function () {
  'use strict';

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Header sticky background ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => {
      const stuck = window.scrollY > 200;
      header.classList.toggle('is-stuck', stuck);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Drawer / overlay helpers ---------- */
  const openDrawer = (el) => {
    if (!el) return;
    el.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const bg = $('[data-overlay-bg]');
    if (bg) bg.classList.add('is-open');
    const focusable = el.querySelector('a, button, input, [tabindex]');
    if (focusable) setTimeout(() => focusable.focus(), 50);
  };
  const closeDrawer = (el) => {
    if (!el) return;
    el.classList.remove('is-open');
    document.body.style.overflow = '';
    const bg = $('[data-overlay-bg]');
    if (bg && !$('.cart-drawer.is-open') && !$('.mobile-menu.is-open') && !$('.search-overlay.is-open'))
      bg.classList.remove('is-open');
  };

  $$('[data-open]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer($(`[data-drawer="${btn.dataset.open}"]`));
    });
  });
  $$('[data-close]').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.close;
      if (target === 'all') {
        $$('.is-open').forEach(el => el.classList.remove('is-open'));
        document.body.style.overflow = '';
      } else {
        closeDrawer($(`[data-drawer="${target}"]`));
      }
    });
  });
  $('[data-overlay-bg]')?.addEventListener('click', () => {
    $$('[data-drawer].is-open').forEach(el => closeDrawer(el));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') $$('[data-drawer].is-open').forEach(el => closeDrawer(el));
  });

  /* ---------- Cart drawer fetch ---------- */
  const setCartBusy = (busy) => {
    const body = $('[data-cart-body]');
    const skel = $('[data-cart-skeleton]');
    if (body) body.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (skel) skel.hidden = !busy;
  };

  let appliedThresholdCode = null; // remember to avoid re-applying every refresh
  const maybeApplyThresholdCode = async () => {
    const tier = $('[data-discount-tier-unlocked]');
    if (!tier) return;
    const code = tier.dataset.discountCode;
    if (!code || appliedThresholdCode === code) return;
    try {
      const cart = await fetch('/cart.js', { cache: 'no-store' }).then(r => r.json());
      const already = (cart.discount_codes || []).some(d => d.code && d.code.toLowerCase() === code.toLowerCase() && d.applicable);
      if (already) { appliedThresholdCode = code; return; }
      await fetch('/discount/' + encodeURIComponent(code), { method: 'GET', credentials: 'same-origin' });
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _tier_check: Date.now().toString() } })
      });
      appliedThresholdCode = code;
    } catch {}
  };

  const refreshCart = async () => {
    const drawerBody = $('[data-cart-body]');
    if (!drawerBody) return;
    try {
      // Shopify Section Rendering API — returns JSON keyed by section name
      // with the fully-rendered section HTML.
      const res = await fetch('/?sections=cart-drawer-content', {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store'
      });
      const data = await res.json();
      const sectionHTML = data['cart-drawer-content'];
      if (sectionHTML) {
        const tmp = document.createElement('div');
        tmp.innerHTML = sectionHTML;
        const wrapper = tmp.querySelector('.shopify-section') || tmp;
        drawerBody.innerHTML = wrapper.innerHTML;
      }
      const cart = await fetch('/cart.js', { cache: 'no-store' }).then(r => r.json());
      const dot = $('[data-cart-dot]');
      if (dot) dot.classList.toggle('is-visible', cart.item_count > 0);
      // If the cart subtotal crossed the discount threshold, auto-apply the code.
      maybeApplyThresholdCode();
    } catch (e) {
      console.warn('cart refresh failed', e);
    } finally {
      setCartBusy(false);
    }
  };

  // Add to cart — open drawer instantly, fetch in background
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action="/cart/add"], form[action^="/cart/add"]')) return;
    e.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const original = submit ? submit.innerHTML : '';
    if (submit) { submit.disabled = true; submit.innerHTML = '<span class="visually-hidden">Adding…</span>•••'; }
    // Open drawer immediately with skeleton
    setCartBusy(true);
    openDrawer($('[data-drawer="cart"]'));
    try {
      const data = new FormData(form);
      const r = await fetch('/cart/add.js', { method: 'POST', body: data, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.description || 'add failed');
      }
      // Subtle haptic confirmation on Android (no-op on iOS)
      if (navigator.vibrate) navigator.vibrate(10);
      await refreshCart();
    } catch (err) {
      setCartBusy(false);
      toast({ message: err.message || 'Could not add to bag. Please try again.', variant: 'error' });
    } finally {
      if (submit) { submit.disabled = false; submit.innerHTML = original; }
    }
  });

  // Cart drawer interactions
  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      setCartBusy(true);
      const key = removeBtn.dataset.cartRemove;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      } catch {}
      refreshCart();
      return;
    }
    const inc = e.target.closest('[data-qty-inc], [data-qty-dec]');
    if (inc) {
      const wrap = inc.closest('[data-cart-line]');
      if (!wrap) return;
      const key = wrap.dataset.cartLine;
      const input = wrap.querySelector('input');
      const dir = inc.matches('[data-qty-inc]') ? 1 : -1;
      const next = Math.max(0, parseInt(input.value, 10) + dir);
      input.value = next;
      setCartBusy(true);
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: next })
        });
      } catch {}
      refreshCart();
      return;
    }
    const removeDiscount = e.target.closest('[data-discount-remove]');
    if (removeDiscount) {
      e.preventDefault();
      setCartBusy(true);
      try {
        // Remove a discount: apply an empty discount via the /discount endpoint
        // by submitting cart update with empty discount_code through cart change
        await fetch('/discount/?redirect=/', { method: 'GET' }).catch(() => {});
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: '' })
        });
      } catch {}
      refreshCart();
    }
  });

  // Discount code apply
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('[data-cart-discount]')) return;
    e.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const code = (input?.value || '').trim();
    if (!code) return;
    setCartBusy(true);
    try {
      // Apply discount via Shopify's /discount/{code} endpoint.
      // Use no-cors mode and a redirect=/cart to keep the user on the page;
      // the server applies the discount cookie.
      await fetch('/discount/' + encodeURIComponent(code), { method: 'GET', credentials: 'same-origin' });
      // Touch the cart so the discount actually evaluates against current items
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _discount_check: Date.now().toString() } })
      });
      await refreshCart();
      // After refresh, check if the discount was actually applied
      const cart = await fetch('/cart.js').then(r => r.json()).catch(() => null);
      const applied = cart && cart.discount_codes && cart.discount_codes.some(d => d.code.toLowerCase() === code.toLowerCase() && d.applicable);
      if (!applied) {
        toast({ message: `"${code}" couldn't be applied.`, variant: 'error' });
      } else {
        if (input) input.value = '';
        toast({ message: 'Discount applied.', variant: 'success' });
      }
    } catch (err) {
      toast({ message: 'Could not apply discount.', variant: 'error' });
      setCartBusy(false);
    }
  });

  /* ---------- Notify-me inline form (stub: posts to /contact for now) ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches('.notify-form')) return;
    e.preventDefault();
    // Hook for Swym integration: window.swymNotifyMe?.show?.()
    if (window.swymNotifyMe && form.dataset.productId) {
      window.swymNotifyMe.show({ productId: form.dataset.productId });
    }
    form.classList.add('is-submitted');
  });

  /* ---------- Predictive search — card grid ---------- */
  (function predictiveSearch() {
    const input = $('[data-predictive-input]');
    const results = $('[data-predictive-results]');
    if (!input || !results) return;

    const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    const formatPrice = (p) => p && p.price ? p.price : '';
    let timer = null, lastQuery = '', controller = null;

    const render = (state, payload) => {
      if (state === 'empty') {
        results.innerHTML = '';
        results.removeAttribute('data-state');
        return;
      }
      if (state === 'loading') {
        results.setAttribute('data-state', 'loading');
        results.innerHTML = `<div class="predictive__status">Searching for "${escapeHtml(payload)}"…</div>`;
        return;
      }
      if (state === 'no-results') {
        results.setAttribute('data-state', 'no-results');
        results.innerHTML = `
          <div class="predictive__status">
            <p>No matches for "<strong>${escapeHtml(payload)}</strong>".</p>
            <a class="btn btn--link" href="/search?q=${encodeURIComponent(payload)}">Search anyway →</a>
          </div>`;
        return;
      }
      // results
      const items = payload.items;
      const q = payload.query;
      results.setAttribute('data-state', 'results');
      results.innerHTML = `
        <div class="predictive__head">
          <span class="eyebrow">Suggestions</span>
          <a class="btn btn--link" href="/search?q=${encodeURIComponent(q)}">See all results →</a>
        </div>
        <div class="predictive__grid">
          ${items.map(p => `
            <a class="predictive-card" href="${escapeHtml(p.url)}">
              <div class="predictive-card__media">
                ${p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
                ${p.available === false ? `<span class="tag tag--soldout">Sold out</span>` : ''}
              </div>
              <div class="predictive-card__info">
                ${p.vendor ? `<span class="predictive-card__eyebrow">${escapeHtml(p.vendor)}</span>` : ''}
                <h4 class="predictive-card__title">${escapeHtml(p.title)}</h4>
                <span class="predictive-card__price">${formatPrice(p)}</span>
              </div>
            </a>
          `).join('')}
        </div>`;
    };

    const search = async (q) => {
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const url = `${window.Shopify ? '' : ''}/search/suggest.json` +
          `?q=${encodeURIComponent(q)}` +
          `&resources[type]=product` +
          `&resources[limit]=8` +
          `&resources[options][unavailable_products]=last` +
          `&resources[options][fields]=title,product_type,variants.title,vendor,tag`;
        const res = await fetch(url, { signal: controller.signal, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('search failed');
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) { render('no-results', q); return; }
        render('results', { items: products, query: q });
      } catch (e) {
        if (e.name === 'AbortError') return;
        render('no-results', q);
      }
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { render('empty'); lastQuery = ''; return; }
      if (q === lastQuery) return;
      lastQuery = q;
      render('loading', q);
      timer = setTimeout(() => search(q), 180);
    });
    // Reset on overlay close
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-close="search"]')) {
        input.value = '';
        lastQuery = '';
        render('empty');
      }
    });
  })();

  /* ---------- Toasts ---------- */
  const toastStack = (() => {
    let s = $('.toast-stack');
    if (!s) { s = document.createElement('div'); s.className = 'toast-stack'; document.body.appendChild(s); }
    return s;
  })();
  function toast({ message, variant = 'info', duration = 4000 } = {}) {
    const el = document.createElement('div');
    el.className = `toast toast--${variant}`;
    el.setAttribute('role', variant === 'error' ? 'alert' : 'status');
    el.textContent = message;
    toastStack.appendChild(el);
    setTimeout(() => el.remove(), duration);
  }
  window.layaly = window.layaly || {};
  window.layaly.toast = toast;
  window.layaly.refreshCart = refreshCart;

  /* ---------- Reveal on scroll ---------- */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -10% 0px' });
    $$('.reveal').forEach(el => io.observe(el));
    // Stagger children of any [data-stagger] container
    $$('[data-stagger]').forEach(parent => {
      Array.from(parent.children).forEach((child, i) => {
        child.classList.add('reveal');
        if (i > 0) child.style.transitionDelay = (i * 60) + 'ms';
        io.observe(child);
      });
    });
  } else {
    $$('.reveal').forEach(el => el.classList.add('is-visible'));
  }

  /* ---------- Accordion ---------- */
  $$('.accordion-trigger').forEach(btn => {
    const item = btn.closest('.accordion-item');
    item?.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', () => {
      const item = btn.closest('.accordion-item');
      item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', item.classList.contains('is-open'));
    });
  });

  /* ---------- PDP gallery — swipe-carousel + sync'd thumbs/dots ---------- */
  (function pdpGallery() {
    const gallery = $('[data-pdp-gallery]');
    if (!gallery) return;
    const track = gallery.querySelector('[data-pdp-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-pdp-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-pdp-thumb]'));
    const dots = Array.from(gallery.querySelectorAll('[data-pdp-dot]'));
    if (!track || slides.length === 0) return;

    const setActive = (index) => {
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    };

    const scrollToIndex = (index) => {
      const slide = slides[index];
      if (!slide) return;
      // Use scrollLeft so it works inside the track viewport without affecting the page
      track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    };

    // Thumb click → scroll the carousel to that slide (works on every breakpoint).
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const i = parseInt(thumb.dataset.pdpThumb, 10);
        if (Number.isFinite(i)) {
          scrollToIndex(i);
          setActive(i);
        }
      });
    });

    // Dot click → same.
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.dataset.pdpDot, 10);
        if (Number.isFinite(i)) {
          scrollToIndex(i);
          setActive(i);
        }
      });
    });

    // Observe slides — whichever is most-visible in the viewport becomes active.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        // Pick the entry with the highest intersection ratio that is currently visible
        let best = null;
        entries.forEach(e => {
          if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) best = e;
        });
        if (best) {
          const i = parseInt(best.target.dataset.pdpSlide, 10);
          if (Number.isFinite(i)) setActive(i);
        }
      }, { root: track, threshold: [0.5, 0.75, 1] });
      slides.forEach(s => io.observe(s));
    }
  })();

  /* ---------- PDP variant pills ---------- */
  (function variantPicker() {
    const groups = $$('[data-variant-group]');
    if (!groups.length) return;
    const variantsScript = $('[data-product-variants]');
    if (!variantsScript) return;
    let variants;
    try { variants = JSON.parse(variantsScript.textContent); } catch { return; }

    const idInput = $('[data-product-id-input]') || $('.pdp-form [name="id"]');
    const priceEl = $('[data-product-price]');
    const cta = $('[data-add-cta]');

    // Sort groups by their option index so selected[] aligns with v.options[]
    const orderedGroups = groups.slice().sort((a, b) => {
      return (parseInt(a.dataset.optionIndex, 10) || 0) - (parseInt(b.dataset.optionIndex, 10) || 0);
    });

    function readSelected() {
      return orderedGroups.map(g => {
        const a = g.querySelector('.variant-pill.is-active:not([disabled])');
        return a ? a.dataset.variantOption : null;
      });
    }

    function updateSelectedVariant() {
      const selected = readSelected();
      const match = variants.find(v => v.options.every((opt, i) => opt === selected[i]));
      if (!match) return;
      if (idInput) idInput.value = match.id;
      if (priceEl && match.price_formatted) priceEl.textContent = match.price_formatted;
      if (cta) {
        if (match.available) {
          cta.textContent = cta.dataset.labelAdd || 'Add to bag';
          cta.disabled = false;
        } else {
          cta.textContent = cta.dataset.labelSold || 'Sold out — notify me';
          cta.disabled = true;
        }
      }
    }

    // Bind one click handler per group; activate clicked pill, ignore disabled
    orderedGroups.forEach(group => {
      group.addEventListener('click', (e) => {
        const pill = e.target.closest('.variant-pill');
        if (!pill || !group.contains(pill)) return;
        if (pill.hasAttribute('disabled') || pill.classList.contains('is-soldout')) return;
        group.querySelectorAll('.variant-pill').forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');
        updateSelectedVariant();
      });
    });
  })();

  /* ---------- Sticky PDP CTA on mobile ---------- */
  const stickyCta = $('[data-pdp-sticky]');
  const gallery = $('[data-pdp-main]');
  if (stickyCta && gallery && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(([entry]) => {
      stickyCta.classList.toggle('is-visible', !entry.isIntersecting);
    });
    io.observe(gallery);
  }

  /* ---------- Header release on banner contact ----------
     The header is sticky at top: 0 until the rising section beneath the hero
     reaches it. At that moment we switch the header wrapper to position:
     absolute pinned at the release Y, so the header lifts off and scrolls up
     with the page. When the user scrolls back up past that point, sticky is
     restored. */
  (function headerRelease() {
    const wrap = $('.site-header-wrap');
    const heroPin = $('.hero-pin');
    if (!wrap || !heroPin) return;

    // The "banner" is the first element after the hero — what physically
    // rises and meets the header.
    let banner = heroPin.nextElementSibling;
    while (banner && banner.offsetHeight === 0) banner = banner.nextElementSibling;
    if (!banner) return;

    let ticking = false;
    let cachedReleaseY = 0;
    let hasClicked = false;

    // === Web Audio: zero-latency click ===
    // Eager init at script load so by the time the user scrolls, the audio
    // context exists, the buffer is decoded, and only the resume() needs a
    // user gesture. Detection of the threshold happens synchronously in the
    // scroll handler (outside requestAnimationFrame) so audio fires within
    // the same frame the threshold is crossed — no rAF latency.
    const Ctx = window.AudioContext || window.webkitAudioContext;
    let audioCtx = null;
    let clickBuffer = null;
    if (Ctx) {
      try { audioCtx = new Ctx({ latencyHint: 'interactive' }); } catch (_) {}
    }
    const url = window.layaly && window.layaly.clickSoundUrl;
    if (audioCtx && url) {
      fetch(url, { cache: 'force-cache' })
        .then(r => r.arrayBuffer())
        .then(ab => audioCtx.decodeAudioData(ab))
        .then(buf => { clickBuffer = buf; })
        .catch(() => {});
    }
    // Resume the context on the very first user interaction (autoplay policy)
    const resumeAudio = () => {
      if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume();
    };
    ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll', 'mousemove'].forEach(evt => {
      window.addEventListener(evt, resumeAudio, { once: true, passive: true, capture: true });
    });

    const playClick = () => {
      if (hasClicked || !audioCtx || !clickBuffer) return;
      hasClicked = true; // set first so a re-entrant scroll cannot re-trigger
      try {
        const src = audioCtx.createBufferSource();
        src.buffer = clickBuffer;
        const gain = audioCtx.createGain();
        gain.gain.value = 0.25;
        src.connect(gain).connect(audioCtx.destination);
        src.start(0);
      } catch (_) { /* swallow */ }
    };

    const measure = () => {
      const headerH = wrap.offsetHeight;
      const bannerDocTop = banner.getBoundingClientRect().top + window.scrollY;
      cachedReleaseY = bannerDocTop - headerH;
    };
    const compute = () => {
      const overshoot = window.scrollY - cachedReleaseY;
      wrap.style.transform = overshoot > 0 ? `translateY(${-overshoot}px)` : '';
      ticking = false;
    };
    // Synchronous click detector — runs every scroll event with no rAF gate
    const checkClick = () => {
      if (!hasClicked && cachedReleaseY > 0 && window.scrollY > cachedReleaseY) {
        playClick();
      }
    };
    const onScroll = () => {
      checkClick(); // synchronous, no-rAF — audio fires same frame as crossover
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(compute);
    };
    const onResize = () => { measure(); compute(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // Recompute the release Y when images load (banner offset may shift)
    window.addEventListener('load', onResize);
    measure();
    compute();
  })();

  /* ---------- Hero slideshow ---------- */
  $$('[data-hero-slides]').forEach(slidesEl => {
    const slides = Array.from(slidesEl.querySelectorAll('.hero__slide'));
    if (slides.length <= 1) return;
    const interval = parseInt(slidesEl.dataset.heroInterval, 10) || 5000;
    const media = slidesEl.parentElement;
    const dotsEl = media.querySelector('[data-hero-dots]');
    const indexEl = media.querySelector('[data-hero-index]');
    let i = 0;
    let timer;
    const total = slides.length;
    const pad = (n) => String(n).padStart(2, '0');
    const dots = slides.map((_, idx) => {
      const b = document.createElement('button');
      b.className = 'hero__dot' + (idx === 0 ? ' is-active' : '');
      b.type = 'button';
      b.setAttribute('aria-label', 'Slide ' + (idx + 1));
      b.addEventListener('click', () => goTo(idx));
      dotsEl?.appendChild(b);
      return b;
    });
    function updateIndex() {
      if (indexEl) indexEl.innerHTML = `${pad(i + 1)}<span>/</span>${pad(total)}`;
    }
    function goTo(n) {
      slides[i].classList.remove('is-active');
      dots[i]?.classList.remove('is-active');
      i = (n + total) % total;
      slides[i].classList.add('is-active');
      dots[i]?.classList.add('is-active');
      updateIndex();
      restart();
    }
    function next() { goTo(i + 1); }
    function restart() { clearInterval(timer); if (!matchMedia('(prefers-reduced-motion: reduce)').matches) timer = setInterval(next, interval); }
    updateIndex();
    restart();
    media.addEventListener('mouseenter', () => clearInterval(timer));
    media.addEventListener('mouseleave', restart);
  });

  /* ---------- Drop-scroll interactive slider ---------- */
  $$('[data-slider]').forEach(slider => {
    const track = slider.querySelector('[data-slider-track]');
    if (!track) return;
    const prev = slider.querySelector('[data-slider-prev]');
    const next = slider.querySelector('[data-slider-next]');
    const cardWidth = () => {
      const first = track.firstElementChild;
      if (!first) return 320;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -cardWidth() * 2, behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: cardWidth() * 2, behavior: 'smooth' }));

    // Drag-to-scroll (mouse only — touch uses native scroll-snap)
    // No pointer capture — keeps inner <a> clickable.
    let isDown = false, hasMoved = false, startX = 0, scrollStart = 0;
    const DRAG_THRESHOLD = 5;

    track.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isDown = true;
      hasMoved = false;
      startX = e.pageX;
      scrollStart = track.scrollLeft;
    });
    document.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (!hasMoved && Math.abs(dx) > DRAG_THRESHOLD) {
        hasMoved = true;
        track.classList.add('is-dragging');
      }
      if (hasMoved) {
        e.preventDefault();
        track.scrollLeft = scrollStart - dx;
      }
    });
    document.addEventListener('mouseup', () => {
      if (!isDown) return;
      isDown = false;
      // small delay so click handler can read hasMoved
      setTimeout(() => { track.classList.remove('is-dragging'); }, 0);
    });
    // Suppress click that happens after a drag
    track.addEventListener('click', (e) => {
      if (hasMoved) {
        e.preventDefault();
        e.stopPropagation();
        hasMoved = false;
      }
    }, true);

    // Disable arrows at edges
    const updateArrows = () => {
      if (!prev || !next) return;
      const max = track.scrollWidth - track.clientWidth - 1;
      prev.toggleAttribute('disabled', track.scrollLeft <= 0);
      next.toggleAttribute('disabled', track.scrollLeft >= max);
    };
    track.addEventListener('scroll', updateArrows, { passive: true });
    requestAnimationFrame(updateArrows);
    window.addEventListener('resize', updateArrows);
  });

  /* ---------- Quick view ---------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quickview]');
    if (!trigger) return;
    e.preventDefault();
    if (navigator.vibrate) navigator.vibrate(10);
    const handle = trigger.dataset.quickview;
    const modal = $('[data-drawer="quickview"]');
    const inner = $('[data-quickview-content]');
    if (!modal || !inner) return;
    inner.innerHTML = '<div style="padding:48px;text-align:center">Loading…</div>';
    openDrawer(modal);
    try {
      const res = await fetch(`/products/${handle}?view=quickview`);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const next = doc.querySelector('[data-quickview-content]');
      if (next) {
        inner.innerHTML = next.innerHTML;
        wireQuickview(inner);
      }
    } catch {
      inner.innerHTML = '<div style="padding:48px;text-align:center">Could not load. Please try again.</div>';
    }
  });

  function wireQuickview(root) {
    /* ----- Gallery: swipe carousel + dot indicators ----- */
    const gallery = root.querySelector('[data-qv-gallery]');
    if (gallery) {
      const track = gallery.querySelector('[data-qv-track]');
      const slides = Array.from(gallery.querySelectorAll('[data-qv-slide]'));
      const dots = Array.from(gallery.querySelectorAll('[data-qv-dot]'));
      const setActive = (i) => {
        dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
      };
      const goTo = (i) => {
        const s = slides[i];
        if (!s || !track) return;
        track.scrollTo({ left: s.offsetLeft, behavior: 'smooth' });
        setActive(i);
      };
      dots.forEach(d => d.addEventListener('click', () => {
        const i = parseInt(d.dataset.qvDot, 10);
        if (Number.isFinite(i)) goTo(i);
      }));
      if (track && slides.length > 1 && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          let best = null;
          entries.forEach(e => {
            if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) best = e;
          });
          if (best) {
            const i = parseInt(best.target.dataset.qvSlide, 10);
            if (Number.isFinite(i)) setActive(i);
          }
        }, { root: track, threshold: [0.5, 0.75, 1] });
        slides.forEach(s => io.observe(s));
      }
    }

    /* ----- Variant pills + price/CTA recalc ----- */
    const variantsScript = root.querySelector('[data-qv-variants]');
    if (!variantsScript) return;
    let variants;
    try { variants = JSON.parse(variantsScript.textContent); } catch { return; }
    const idField = root.querySelector('[data-qv-id]');
    const priceEl = root.querySelector('[data-qv-price]');
    const cta = root.querySelector('[data-qv-cta]');
    const groups = Array.from(root.querySelectorAll('[data-qv-group]'));
    const orderedGroups = groups.slice().sort((a, b) => {
      return (parseInt(a.dataset.optionIndex, 10) || 0) - (parseInt(b.dataset.optionIndex, 10) || 0);
    });

    function recalc() {
      const selected = orderedGroups.map(g => {
        const a = g.querySelector('.variant-pill.is-active:not([disabled])');
        return a ? a.dataset.qvOption : null;
      });
      const match = variants.find(v => v.options.every((opt, i) => opt === selected[i]));
      if (!match) return;
      if (idField) idField.value = match.id;
      if (priceEl) {
        priceEl.innerHTML = match.compare_at && match.compare_at !== match.price
          ? `${match.price} <span class="pdp-info__price--was">${match.compare_at}</span>`
          : match.price;
      }
      if (cta) {
        if (match.available) {
          cta.textContent = 'Add to bag';
          cta.disabled = false;
        } else {
          cta.textContent = 'Sold out';
          cta.disabled = true;
        }
      }
    }
    orderedGroups.forEach(group => {
      group.addEventListener('click', (ev) => {
        const pill = ev.target.closest('.variant-pill');
        if (!pill || !group.contains(pill)) return;
        if (pill.hasAttribute('disabled') || pill.classList.contains('is-soldout')) return;
        group.querySelectorAll('.variant-pill').forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');
        recalc();
      });
    });
  }
})();
